import React from 'react';
import { Typography, Table, Button, InputNumber, Tag, Modal, Col } from 'antd';
import { Storage, TextFormat } from 'react-jhipster';
import { getMoyenPaiementColor, getStatusColorSalaire } from 'app/utils/colorUtils';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faPrint } from '@fortawesome/free-solid-svg-icons';
import { hasAnyAuthority } from 'app/shared/auth/private-route';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { APP_DATE_FORMAT, AUTHORITIES } from 'app/config/constants';
import { ISalaire, SalaireDetails, StatutSalaire } from '../../shared/model/salaire.model';
import axios from 'axios';
import { getPaymentMethod } from 'app/utils/paymentUtils';
import { relanceSalairePayment } from './salaire.reducer';
import { Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import { useCheckIsAnneeInactive } from 'app/hooks/useCheckIsAnneeActive';

const { Title } = Typography;

interface DetailsSalaireProps {
  salaire: SalaireDetails;
  ecoleId: string;
  anneeScolaireId: string;
  handleMontantEdit: () => void;
  handleConfirm: () => void;
  statutSalaire: StatutSalaire;
  setStatutSalaire: (value: React.SetStateAction<StatutSalaire>) => void;
  editingMontantNet: boolean;
  montantNet: number;
  setMontantNet: React.Dispatch<React.SetStateAction<number>>;
}

const DetailsSalaire: React.FC<DetailsSalaireProps> = ({
  ecoleId,
  salaire,
  handleMontantEdit,
  handleConfirm,
  montantNet,
  setMontantNet,
  editingMontantNet,
  anneeScolaireId,
}) => {
  const isAdmin = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.ADMIN]));
  const isComptable = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.COMPTABLE]));
  const isDirecteur = useAppSelector(state => hasAnyAuthority(state.authentication.account.authorities, [AUTHORITIES.DIRECTEUR]));
  const inactiveAnnee = useCheckIsAnneeInactive(anneeScolaireId);
  const hasAccess = isAdmin || isDirecteur || isComptable;
  const columns = [
    { title: '', dataIndex: 'label' },
    { title: '', dataIndex: 'value' },
  ];

  const getStatusLabel = status => {
    switch (status) {
      case 'NON_PAYEE':
        return <Tag color="red">Non Payé</Tag>;
      case 'PAYEE':
        return <Tag color="green">Payé</Tag>;
      case 'EN_ATTENTE':
        return <Tag color="blue">En Attente</Tag>;
      default:
        return <Tag color="#999">Créé</Tag>;
    }
  };

  const data = [
    { key: '1', label: 'Numéro Salaire', value: <span>{salaire.numeroSalaire}</span> },
    { key: '2', label: 'Professeur', value: <span>{`${salaire.professeur}`}</span> },
    { key: '3', label: 'Téléphone', value: <span>{salaire.numeroTelephonePaiement}</span> },
    {
      key: '4',
      label: 'Montant Brut',
      value: editingMontantNet ? (
        <span className="space-button">
          <InputNumber value={montantNet} onChange={value => setMontantNet(value)} style={{ width: '80%', marginRight: '8px' }} />
          <Button type="primary" onClick={handleConfirm} style={{ marginRight: '4px' }}>
            Confirmer
          </Button>
          <Button onClick={handleMontantEdit}>Annuler</Button>
        </span>
      ) : (
        <div className="space-button">
          <span>{`${Number(salaire.montant)} FCFA`}</span>
          {hasAccess && salaire.statutSalaire === 'CREE' && (
            <Button
              disabled={inactiveAnnee}
              className="button-right"
              type="primary"
              onClick={handleMontantEdit}
              icon={<FontAwesomeIcon icon={faEdit} />}
            ></Button>
          )}
        </div>
      ),
    },
    {
      key: '5',
      label: 'Retenue BRS',
      value: (
        <div className="space-button">
          <span>{`${Number(salaire.retenue)} FCFA`}</span>
        </div>
      ),
    },
    {
      key: '6',
      label: 'Montant Net',
      value: (
        <div className="space-button">
          <span>{`${Number(salaire.net)} FCFA`}</span>
        </div>
      ),
    },
    {
      key: '7',
      label: 'Frais',
      value: (
        <div className="space-button">
          <span>{`${Number(salaire.frais)} FCFA`}</span>
        </div>
      ),
    },

    {
      key: '8',
      label: 'Date de création',
      value: <TextFormat type="date" value={salaire.dateEmission} format={APP_DATE_FORMAT} />,
    },
    {
      key: '9',
      label: 'Statut',
      value: (
        <div className="space-button">
          <span style={{ color: getStatusColorSalaire(salaire?.statutSalaire), fontWeight: salaire?.statutSalaire ? 'bold' : '' }}>
            {getStatusLabel(salaire?.statutSalaire)}
          </span>
        </div>
      ),
    },
    {
      key: '10',
      label: 'Date de Paiement',
      value: salaire.datePaiement ? <TextFormat type="date" value={salaire.datePaiement} format={APP_DATE_FORMAT} /> : 'En Attente',
    },
    {
      key: '11',
      label: 'Moyen de Paiement',
      value: (
        <span style={{ color: getMoyenPaiementColor(salaire.moyenPaiement), fontWeight: salaire.moyenPaiement ? 'bold' : '' }}>
          {salaire.moyenPaiement ? getPaymentMethod(salaire.moyenPaiement) : 'En Attente'}
        </span>
      ),
    },
  ];

  return (
    <>
      <Table
        sticky
        showHeader={false}
        title={() => <TableTitle salaire={salaire} ecoleId={ecoleId} inactiveAnnee={inactiveAnnee} />}
        columns={columns}
        dataSource={data}
        pagination={false}
        bordered
        rowClassName="facture-table"
      />
    </>
  );
};

export default DetailsSalaire;

const TableTitle = ({ salaire, ecoleId, inactiveAnnee }) => {
  const token = Storage.local.get('jhi-authenticationToken') || Storage.session.get('jhi-authenticationToken');
  const dispatch = useAppDispatch();
  const dowloadPDF = () => {
    axios({
      url: `/api/salaire/${salaire.id}/print`,
      method: 'GET',
      responseType: 'blob',
      headers: {
        Authorization: 'Bearer ' + token,
      },
    })
      .then(response => {
        const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
        window.open(url);
      })
      .catch(error => {
        console.error('Erreur lors du telechargement  : ', error);
      });
  };

  return (
    <Row>
      <Title level={5}>
        <span>
          Fiche N° <span className="invoice-title">{salaire.numeroSalaire}</span>
        </span>
      </Title>
      <Col offset={10}>
        <Button
          disabled={salaire.statutSalaire === StatutSalaire.PAYEE || inactiveAnnee}
          style={{ backgroundColor: inactiveAnnee ? '#ccc' : '#f28e19', color: 'white', fontWeight: 'bold' }}
        >
          <Link to={`/salaires/${ecoleId}/${salaire.id}/details/relance-paiement`}>Relancer le paiement</Link>
        </Button>
      </Col>
      <Col>
        <Button type="link" onClick={dowloadPDF} disabled>
          <FontAwesomeIcon icon={faPrint} size={'lg'} />
        </Button>
      </Col>
    </Row>
  );
};
